/**
 * One risk scale, defined once.
 *
 * Three coexisted: this table banded at 85/60/35, the backend called a client
 * high-risk at 70, and the dashboard status pill used no number at all. Nothing
 * on the page defined any of them, so "At risk · 77" could sit above
 * "High-risk cases: 1" and both be right. Every surface that shows a risk score
 * reads these bands.
 *
 * `null` is its own state and never a band: a scan that measured nothing
 * arrives with no score, and must not be badged green.
 */

export const RISK_BANDS = [
	{ key: 'critical', label: 'Critical', min: 85, max: 100, badge: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300', dot: 'bg-red-600', text: 'text-red-700' },
	{ key: 'atRisk', label: 'At risk', min: 60, max: 84, badge: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300', dot: 'bg-orange-500', text: 'text-orange-700' },
	{ key: 'watch', label: 'Watch', min: 35, max: 59, badge: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300', dot: 'bg-amber-500', text: 'text-amber-700' },
	{ key: 'clear', label: 'Clear', min: 0, max: 34, badge: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300', dot: 'bg-green-600', text: 'text-green-700' },
];

/** Shown wherever a score is absent. Not a band - it has no range. */
export const NO_SCORE = {
	key: 'none',
	label: 'No score',
	badge: 'text-muted-foreground',
	dot: 'bg-input',
	text: 'text-muted-foreground',
};

/**
 * @param {number|null|undefined} score
 * @returns the matching band, or NO_SCORE when there is nothing to band.
 */
export function riskBand(score) {
	if (typeof score !== 'number' || Number.isNaN(score)) return NO_SCORE;
	return RISK_BANDS.find((b) => score >= b.min) || RISK_BANDS[RISK_BANDS.length - 1];
}

/** "Critical · 91", or "Clear" - the number adds nothing to a clean result. */
export function riskLabel(score) {
	const band = riskBand(score);
	if (band.key === 'none') return NO_SCORE.label;
	if (band.key === 'clear') return band.label;
	return `${band.label} · ${score}`;
}
